// Geometry for scale-drawn event maps. A map with `drawnToScale` + valid
// geographic bounds is north-up and axis-aligned, so its 0-1 image fractions
// (the space every event-map overlay uses — resource/incident
// eventMapCoordinates, map pins) convert linearly to and from lng/lat. That
// is what lets the overlay view place the site plan on the live map, and
// lets a pin dropped on the plan give responders a real GPS position.
//
// Mirrored in mobile/src/utils/cadLocation.js — keep the two in sync.

const validBounds = (bounds) => {
  if (!bounds) return false
  const { north, south, east, west } = bounds
  return (
    [north, south, east, west].every((value) => Number.isFinite(Number(value))) &&
    Number(north) !== Number(south) &&
    Number(east) !== Number(west)
  )
}

export const hasScaledEventMap = (eventMap) =>
  !!eventMap?.s3key && !!eventMap?.drawnToScale && validBounds(eventMap?.bounds)

// Which view to open first: overlay if the map is to scale, otherwise the
// site plan, otherwise the plain geographic map.
export const preferredMapView = (eventMap) => {
  if (hasScaledEventMap(eventMap)) return 'overlay'
  if (eventMap?.s3key) return 'event'
  return 'map'
}

// Downgrade a requested view to the best one the room can actually show.
export const resolveMapView = (requested, eventMap) => {
  // The GPX view only exists on the control page itself — consumers like
  // IncidentLocationPicker fall back to the plain geographic map.
  if (requested === 'gpx') return 'map'
  if (requested === 'overlay' && !hasScaledEventMap(eventMap)) {
    return eventMap?.s3key ? 'event' : 'map'
  }
  if (requested === 'event' && !eventMap?.s3key) return 'map'
  return requested || 'map'
}

// Image corners in MapLibre image-source order: TL, TR, BR, BL.
export const eventMapCorners = (eventMap) => {
  const { north, south, east, west } = eventMap.bounds
  return [
    [Number(west), Number(north)],
    [Number(east), Number(north)],
    [Number(east), Number(south)],
    [Number(west), Number(south)],
  ]
}

// Bounds in the shape fitBounds() wants: [[west, south], [east, north]].
// (Not the four image corners above — MapLibre reads those as NaNs.)
export const eventMapLngLatBounds = (eventMap) => {
  const { north, south, east, west } = eventMap.bounds
  return [
    [Number(west), Number(south)],
    [Number(east), Number(north)],
  ]
}

export const eventMapCenter = (eventMap) => {
  const { north, south, east, west } = eventMap.bounds
  return [
    (Number(east) + Number(west)) / 2,
    (Number(north) + Number(south)) / 2,
  ]
}

// 0-1 image fraction pair -> [lng, lat]. Null when the map isn't to scale.
export const eventMapToLngLat = (eventMap, fraction) => {
  if (!hasScaledEventMap(eventMap) || fraction?.length !== 2) return null
  const { north, south, east, west } = eventMap.bounds
  const [x, y] = fraction.map(Number)
  if (![x, y].every(Number.isFinite)) return null
  return [
    Number(west) + x * (Number(east) - Number(west)),
    Number(north) - y * (Number(north) - Number(south)),
  ]
}

// [lng, lat] -> 0-1 image fraction pair, or null when the point falls outside
// the map image (or the map isn't to scale).
export const lngLatToEventMap = (eventMap, lngLat) => {
  if (!hasScaledEventMap(eventMap) || lngLat?.length !== 2) return null
  const { north, south, east, west } = eventMap.bounds
  const [lng, lat] = lngLat.map(Number)
  const x = (lng - Number(west)) / (Number(east) - Number(west))
  const y = (Number(north) - lat) / (Number(north) - Number(south))
  if (![x, y].every(Number.isFinite) || x < 0 || x > 1 || y < 0 || y > 1) {
    return null
  }
  return [Number(x.toFixed(4)), Number(y.toFixed(4))]
}

// "lng,lat" string in the format the incident form stores.
export const formatLngLat = (lngLat) =>
  `${Number(lngLat[0]).toFixed(6)},${Number(lngLat[1]).toFixed(6)}`
